import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { TimeGranularityName } from "app/common/utils/timeGranularity";
import { isTimeGranularityName } from "app/common/utils/timeGranularity";
import { dashboardWizardDataModeChanged } from "app/components/dashboard/widget-wizard/actions/dashboardWizardActionCreators";
import { MARKET_SHARES_GRAPH_METRIC_TYPE } from "app/components/dashboard/widget-wizard/components/MarketAnalysis/constants";
import { selectWidgetFilters } from "app/components/dashboard/widget-wizard/components/segments/selectors";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { selectDashboardWizard } from "app/components/dashboard/widget-wizard/selectors/dashboardSelectors";
import { translate } from "app/filters/i18nFilter";
import { dashboardDataDisplayType } from "app/pages/industry-analysis/players/market-shares/utils/utils";

type SelectDataMode = {
    id: string;
    text: string;
};

type TimeGranularityFilterItem = {
    id: TimeGranularityName;
    text: string;
    key?: string;
    disabled?: boolean;
};

type AvailableFilters = {
    timeGranularity: TimeGranularityFilterItem[];
};

type MarketAnalysisWidgetFiltersProps = {
    availableFilters: AvailableFilters;
    changeFilter?: (item: unknown) => void;
    widgetDuration?: string;
};

const MarketAnalysisWidgetFilters = ({
    availableFilters,
    changeFilter,
    widgetDuration,
}: MarketAnalysisWidgetFiltersProps) => {
    const dispatch = useDispatch();

    const widgetFilters = useSelector(selectWidgetFilters);
    const dashboardWizard = useSelector(selectDashboardWizard);

    const isGraphMetric = dashboardWizard.widget?.type === MARKET_SHARES_GRAPH_METRIC_TYPE;
    const granularityItems =
        availableFilters.timeGranularity && availableFilters.timeGranularity.length > 0
            ? availableFilters.timeGranularity.map((item) => ({
                  ...item,
                  disabled: isGraphMetric && widgetDuration === "28d" && item.id === "Monthly",
              }))
            : [{ id: "Monthly" as const, text: "Monthly" }];
    const hasComparableGranularityOptions = granularityItems.length > 1;
    const timeGranularityDisabled =
        !isGraphMetric || !hasComparableGranularityOptions || Boolean(dashboardWizard.widget.comparedDuration);

    useEffect(() => {
        if (!dashboardWizard.widget?.dataMode) {
            const derived = dashboardDataDisplayType[widgetFilters.chartDisplayType] ?? "percent";
            onSelectDataMode({ id: derived, text: derived });
        }
    }, []);

    const onSelectDataMode = (item: SelectDataMode) => {
        dispatch(dashboardWizardDataModeChanged(dashboardWizard, item.id));
    };

    const onSelectTimeGranularity = (item: TimeGranularityFilterItem) => {
        if (!changeFilter || timeGranularityDisabled) {
            return;
        }
        const mergedFilters = Object.entries(widgetFilters)
            .filter(([name]) => name !== "timeGranularity")
            .map(([name, value]) => ({ name, value }));
        mergedFilters.push({ name: "timeGranularity", value: item.id });
        changeFilter(mergedFilters);
    };

    const timeGranularityFilter = widgetFilters.timeGranularity
    const selectedTimeGranularity = isTimeGranularityName(timeGranularityFilter)
        ? timeGranularityFilter
        : (granularityItems[0]?.id ?? "Monthly");

    return (
        <div
            key="dynamicFiltersContainer"
            className="filters"
        >
            <div
                key="includeSubDomains"
                className="dynamicFilter"
                data-automation="market-analysis-widget-filters-time-granularity"
            >
                <h5>{translate("home.dashboards.wizard.filters.TimeGranularity")}</h5>
                <StatefulDropdown
                    key="timeGranularityFilter"
                    disabled={timeGranularityDisabled}
                    items={granularityItems}
                    selectedId={timeGranularityDisabled ? "Monthly" : selectedTimeGranularity}
                    onSelect={onSelectTimeGranularity}
                />
            </div>
            <div
                key="dataMode"
                className="dynamicFilter"
            >
                <h5>{translate(`home.dashboards.wizard.filters.Metric`)}</h5>
                <StatefulDropdown
                    key="dataMode"
                    items={[
                        { key: "percent", id: "percent", text: "% Relative" },
                        { key: "number", id: "number", text: "# Absolute" },
                    ]}
                    selectedId={dashboardWizard.widget.dataMode || "percent"}
                    onSelect={onSelectDataMode}
                />
            </div>
        </div>
    );
};

export default MarketAnalysisWidgetFilters;
